import React, { useState, useEffect } from 'react';
import { useRouter } from 'expo-router';
import { CCChatBubbleIcon } from './CCChatBubbleIcon';
import { getUserStorage, getCurrentUserId } from '../utils/userStorage';

// Chat Context Types
interface ChatContext {
  type: 'goal' | 'plan' | 'workout' | 'mobility' | 'progress' | 'schedule' | 'general' | 'research' | 'research_event' | 'health' | 'sleep' | 'rest_day';
  id?: string;
  entity_id?: string;
  title?: string;
  description?: string;
  action?: 'view' | 'complete' | 'modify' | 'question' | 'concern' | 'progress' | 'discuss';
  additionalContext?: string;
  metadata?: Record<string, any>;  // 🎯 NEW: Metadata for rich pill display
}

// Enhanced Chat Button Component
export const ContextualChatButton: React.FC<{
  context: ChatContext;
  customMessage?: string;
  text?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'secondary' | 'minimal' | 'clean' | 'pill';
  className?: string;
}> = ({ 
  context, 
  customMessage, 
  text = "", // 🎯 MISSION 29 (Jan 23, 2026): Default to icon-only
  size = 'medium',
  variant = 'clean',
  className = ''
}) => {
  const router = useRouter();
  const [isHovering, setIsHovering] = useState(false);

  // 💰 Sprint 7 q8: Check if chat buttons should be greyed out (user past message limit)
  const [chatDisabled, setChatDisabled] = useState(false);

  useEffect(() => {
    const checkDisabled = async () => {
      const userId = getCurrentUserId();
      if (userId && userId !== 'default_user') {
        const val = await getUserStorage('cedar_chat_buttons_disabled', userId);
        setChatDisabled(val === 'true');
      }
    };

    checkDisabled();

    // Listen for real-time changes from Chat.tsx (same-tab custom event)
    const handleChange = () => checkDisabled();
    window.addEventListener('cedar-subscription-change', handleChange);
    return () => window.removeEventListener('cedar-subscription-change', handleChange);
  }, []);

  // 🚨 DEFENSIVE GUARD (Feb 3, 2026): Prevent crash if context is undefined
  if (!context) {
    console.error('🚨 ContextualChatButton: context prop is undefined!');
    console.error('   Props received:', { context, customMessage, text, size, variant, className });
    // Return invisible placeholder to prevent UI break
    return null;
  }

  // 🎯 ISSUE #2 FIX: Navigate directly to chat with deep link context (skip modal)
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card clicks

    // 💰 Sprint 7 q8: Block navigation when chat buttons are disabled (user past limit)
    if (chatDisabled) return;

    // 🔍 DEBUG LOG (Feb 3, 2026): Log context before navigation
    console.log('🎯 ContextualChatButton: Button clicked');
    console.log('   Context type:', context.type);
    console.log('   Context ID:', context.id);
    console.log('   Context title:', context.title);
    
    // Haptic feedback (if supported)
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }

    // Navigate directly to chat with context (Expo Router — params as search strings)
    // Backend will generate proactive message automatically
    router.push({
      pathname: '/chat',
      params: {
        from: 'contextual-launch',
        contextData: JSON.stringify({
          type: context.type,
          id: context.id || '',
          title: context.title || 'Item',
          description: context.description,
          action: context.action,
          metadata: context.metadata || {}
        })
      }
    });
  };

  const sizeClasses = {
    small: 'h-7 px-2.5 text-xs gap-1.5',
    medium: 'h-8 px-3 text-sm gap-2',
    large: 'h-10 px-4 text-base gap-2.5'
  };

  const iconSize = size === 'small' ? 'small' : size === 'large' ? 'large' : 'medium';

  // 💬 PILL VARIANT: Stylized dark green pill with chat icon + "Chat" text
  if (variant === 'pill') {
    return (
      <button
        onClick={handleClick}
        disabled={chatDisabled}
        aria-disabled={chatDisabled}
        title={chatDisabled ? 'Chat with Cedar is paused until your messages reset' : 'Chat with Cedar about this'}
        className={`transition-all duration-150 ${className}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 13px 6px 10px',
          backgroundColor: chatDisabled ? '#B0B0B0' : '#1F6C4F',
          borderRadius: '20px',
          border: 'none',
          cursor: chatDisabled ? 'default' : 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
        onMouseEnter={chatDisabled ? undefined : (e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#113C2B';
        }}
        onMouseLeave={chatDisabled ? undefined : (e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1F6C4F';
        }}
        onTouchStart={chatDisabled ? undefined : (e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#113C2B';
          if (navigator.vibrate) navigator.vibrate(10);
        }}
        onTouchEnd={chatDisabled ? undefined : (e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1F6C4F';
        }}
      >
        {/* Two overlapping speech bubbles icon */}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, opacity: chatDisabled ? 0.6 : 1 }}>
          {/* Back bubble */}
          <path d="M21 6.5C21 5.4 20.1 4.5 19 4.5H8C6.9 4.5 6 5.4 6 6.5V14.5C6 15.6 6.9 16.5 8 16.5H9V19L13 16.5H19C20.1 16.5 21 15.6 21 14.5V6.5Z" fill="white" opacity="0.55"/>
          {/* Front bubble */}
          <path d="M17 2H6C4.34 2 3 3.34 3 5V11C3 12.66 4.34 14 6 14H7V17L11.33 14H17C18.66 14 20 12.66 20 11V5C20 3.34 18.66 2 17 2Z" fill="white"/>
        </svg>
        <span style={{ fontSize: '13px', fontWeight: 600, color: 'white', opacity: chatDisabled ? 0.6 : 1, letterSpacing: '0.01em' }}>
          {text || 'Chat'}
        </span>
      </button>
    );
  }

  // 🎯 MISSION 29 (Jan 23, 2026): CC bubble IS the button (mobile press state)
  if (variant === 'clean') {
    return (
      <button
        onClick={handleClick}
        className={`transition-all duration-150 ${className}`}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: chatDisabled ? 'default' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          WebkitTapHighlightColor: 'transparent', // Remove default mobile tap highlight
        }}
        // Mobile: touch press state
        onTouchStart={chatDisabled ? undefined : (e) => {
          const bubblePath = e.currentTarget.querySelector('svg .cc-bubble-bg');
          if (bubblePath) (bubblePath as SVGPathElement).setAttribute('fill', '#4A9B8E');
          if (navigator.vibrate) navigator.vibrate(10);
        }}
        onTouchEnd={chatDisabled ? undefined : (e) => {
          const bubblePath = e.currentTarget.querySelector('svg .cc-bubble-bg');
          if (bubblePath) (bubblePath as SVGPathElement).setAttribute('fill', '#1F6C4F');
        }}
        // Web: hover and focus for visible feedback (Feb 2026)
        onMouseEnter={chatDisabled ? undefined : (e) => {
          const bubblePath = e.currentTarget.querySelector('svg .cc-bubble-bg');
          if (bubblePath) (bubblePath as SVGPathElement).setAttribute('fill', '#4A9B8E');
        }}
        onMouseLeave={chatDisabled ? undefined : (e) => {
          const bubblePath = e.currentTarget.querySelector('svg .cc-bubble-bg');
          if (bubblePath) (bubblePath as SVGPathElement).setAttribute('fill', chatDisabled ? '#B0B0B0' : '#1F6C4F');
        }}
        onFocus={chatDisabled ? undefined : (e) => {
          const bubblePath = e.currentTarget.querySelector('svg .cc-bubble-bg');
          if (bubblePath) (bubblePath as SVGPathElement).setAttribute('fill', '#4A9B8E');
        }}
        onBlur={chatDisabled ? undefined : (e) => {
          const bubblePath = e.currentTarget.querySelector('svg .cc-bubble-bg');
          if (bubblePath) (bubblePath as SVGPathElement).setAttribute('fill', chatDisabled ? '#B0B0B0' : '#1F6C4F');
        }}
        disabled={chatDisabled}
        aria-disabled={chatDisabled}
        title={chatDisabled ? 'Chat with Cedar is paused until your messages reset' : undefined}
      >
        <CCChatBubbleIcon
          size={size}
          className="flex-shrink-0"
          style={{
            display: 'block',
          }}
          disabled={chatDisabled}
        />
        {text && <span className="truncate ml-2" style={{ fontSize: '13px', fontWeight: 500, opacity: chatDisabled ? 0.45 : 1 }}>{text}</span>}
      </button>
    );
  }

  // Legacy variants for backward compatibility - now with Cedar colors
  const variantClasses = {
    primary: '', // Handled with inline styles
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300',
    minimal: '', // Handled with inline styles
    clean: '' // handled above
  };

  // Get variant-specific styles
  const getVariantStyles = () => {
    if (variant === 'primary') {
      return {
        backgroundColor: '#5FA287', /* Light Cedar */
        color: 'white',
        border: 'none',
        hoverBg: '#42A5A5', /* Cedar Stream on hover */
      };
    }
    if (variant === 'minimal') {
      return {
        backgroundColor: 'transparent',
        color: '#1f6c4f', /* Cedar primary */
        border: '1px solid #1f6c4f',
        hoverBg: '#e8f2ed', /* Cedar subtle */
      };
    }
    return null;
  };

  const variantStyles = getVariantStyles();

  // 🎯 MISSION 29 (Jan 23, 2026): CC bubble IS the button (mobile press state)
  return (
    <button
      onClick={handleClick}
      className={`transition-all duration-150 ${className}`}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: chatDisabled ? 'default' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: text ? '8px' : '0',
        WebkitTapHighlightColor: 'transparent', // Remove default mobile tap highlight
      }}
      // Mobile-centric press state (no hover) — disabled when past limit (q8)
      onTouchStart={chatDisabled ? undefined : (e) => {
        const icon = e.currentTarget.querySelector('svg rect');
        if (icon) (icon as SVGRectElement).setAttribute('fill', '#4A9B8E'); // Blue-teal press
      }}
      onTouchEnd={chatDisabled ? undefined : (e) => {
        const icon = e.currentTarget.querySelector('svg rect');
        if (icon) (icon as SVGRectElement).setAttribute('fill', '#1F6C4F'); // Back to green
      }}
      disabled={chatDisabled}
      aria-disabled={chatDisabled}
      title={chatDisabled ? 'Chat with Cedar is paused until your messages reset' : undefined}
    >
      <CCChatBubbleIcon
        size={size}
        className="flex-shrink-0"
        style={{ 
          display: 'block',
        }}
        disabled={chatDisabled}
      />
      {text && <span className="truncate" style={{ display: 'inline-block', fontSize: '13px', fontWeight: 500, opacity: chatDisabled ? 0.45 : 1 }}>{text}</span>}
    </button>
  );
}; 